import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import {
  SITE_AREAS,
  SITE_AREA_MAP,
  ALL_AREAS,
  NOTICE_SEVERITIES,
  NOTICE_SEVERITY_MAP,
} from '../status/statusAreas';
import './AdminNotices.css';

/** Blank form state for creating a new notice. */
const EMPTY_FORM = {
  title: '',
  message: '',
  severity: 'warning',
  areas: [ALL_AREAS],
  is_active: true,
  dismissible: true,
  starts_at: '',
  ends_at: '',
  link_url: '',
  link_label: '',
};

/** Convert an ISO datetime (or '') to a value usable by <input type=datetime-local>. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Convert a datetime-local value back to an ISO string, or null when empty. */
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

/**
 * Admin panel for site-wide status notices (driftnotiser). Lists every notice
 * (active + inactive) and provides a create/edit form covering severity, the
 * targeted site areas, an optional scheduling window and link, plus a
 * dismissible toggle. All requests go to the stats-backend ``/api/notices``
 * proxy, which forwards the caller's JWT so the Django API enforces the
 * ``notices.manage`` permission.
 *
 * @param {object} props - Component props.
 * @param {object} props.perms - Admin permission helper from useAdminPermissions.
 * @returns {JSX.Element} The notices admin panel.
 */
export default function AdminNotices({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const canManage = perms?.isOwner || perms?.hasCommand?.('notices.manage');

  const loadNotices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/notices/admin`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('load failed');
      setNotices(await r.json());
      setError('');
    } catch {
      setError(t('notices.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    if (canManage) loadNotices();
    else setLoading(false);
  }, [canManage, loadNotices]);

  /** Reset the form to create-mode (no notice selected). */
  const startNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  /** Populate the form from an existing notice for editing. */
  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({
      title: n.title || '',
      message: n.message || '',
      severity: n.severity || 'warning',
      areas: Array.isArray(n.areas) && n.areas.length ? n.areas : [ALL_AREAS],
      is_active: !!n.is_active,
      dismissible: !!n.dismissible,
      starts_at: toLocalInput(n.starts_at),
      ends_at: toLocalInput(n.ends_at),
      link_url: n.link_url || '',
      link_label: n.link_label || '',
    });
    setError('');
  };

  /** Update one form field by name. */
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  /**
   * Toggle an area checkbox. Selecting "all" clears the rest; selecting a
   * specific area clears "all" so the two states stay mutually exclusive.
   */
  const toggleArea = (key) => {
    setForm((f) => {
      if (key === ALL_AREAS) return { ...f, areas: [ALL_AREAS] };
      const withoutAll = f.areas.filter((a) => a !== ALL_AREAS && a !== key);
      const next = f.areas.includes(key) ? withoutAll : [...withoutAll, key];
      return { ...f, areas: next.length ? next : [ALL_AREAS] };
    });
  };

  /** Create or update the notice. */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError(t('notices.titleRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        areas: form.areas.length ? form.areas : [ALL_AREAS],
        starts_at: fromLocalInput(form.starts_at),
        ends_at: fromLocalInput(form.ends_at),
      };
      const method = editingId ? 'PATCH' : 'POST';
      const url = editingId
        ? `${API_URL}/api/notices/admin/${editingId}`
        : `${API_URL}/api/notices/admin`;
      const r = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data?.error === 'string' ? data.error : t('notices.saveFailed'));
      }
      await loadNotices();
      startNew();
    } catch (err) {
      setError(err.message || t('notices.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Toggle the active flag inline from the list. */
  const handleToggleActive = async (n) => {
    try {
      const r = await fetch(`${API_URL}/api/notices/admin/${n.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !n.is_active }),
      });
      if (!r.ok) throw new Error('toggle failed');
      await loadNotices();
    } catch {
      setError(t('notices.updateFailed'));
    }
  };

  /** Delete a notice after confirmation. */
  const handleDelete = async (n) => {
    if (!window.confirm(t('notices.deleteConfirm', { title: n.title }))) return;
    try {
      const r = await fetch(`${API_URL}/api/notices/admin/${n.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!r.ok && r.status !== 204) throw new Error('delete failed');
      if (editingId === n.id) startNew();
      await loadNotices();
    } catch {
      setError(t('notices.deleteFailed'));
    }
  };

  /** Render a notice's targeted areas as a readable label. */
  const areaSummary = (areas) => {
    if (!Array.isArray(areas) || !areas.length || areas.includes(ALL_AREAS)) {
      return t('notices.areaAll');
    }
    return areas.map((k) => (SITE_AREA_MAP[k] ? t(SITE_AREA_MAP[k].labelKey) : k)).join(', ');
  };

  if (!canManage) {
    return <div className="admin-notices"><p className="notices-admin-empty">{t('admin.noAccess')}</p></div>;
  }

  return (
    <div className="admin-notices">
      <div className="notices-admin-header">
        <h2>{t('notices.adminTitle')}</h2>
        {editingId && (
          <button type="button" className="notices-btn notices-btn-ghost" onClick={startNew}>
            + {t('notices.newNotice')}
          </button>
        )}
      </div>

      <p className="notices-admin-hint">{t('notices.adminHint')}</p>

      {error && <div className="notices-admin-error">{error}</div>}

      <form className="notices-admin-form" onSubmit={handleSave}>
        <h3>{editingId ? t('notices.editNotice') : t('notices.newNotice')}</h3>

        <label className="notices-field">
          <span>{t('notices.titleLabel')}</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            maxLength={120}
            placeholder={t('notices.titlePlaceholder')}
          />
        </label>

        <label className="notices-field">
          <span>{t('notices.messageLabel')}</span>
          <textarea
            value={form.message}
            onChange={(e) => setField('message', e.target.value)}
            maxLength={500}
            rows={2}
            placeholder={t('notices.messagePlaceholder')}
          />
        </label>

        <div className="notices-field-row">
          <label className="notices-field">
            <span>{t('notices.severityLabel')}</span>
            <select value={form.severity} onChange={(e) => setField('severity', e.target.value)}>
              {NOTICE_SEVERITIES.map((s) => (
                <option key={s.value} value={s.value}>{s.emoji} {t(s.labelKey)}</option>
              ))}
            </select>
          </label>
        </div>

        <fieldset className="notices-areas">
          <legend>{t('notices.areasLabel')}</legend>
          <div className="notices-areas-grid">
            {SITE_AREAS.map((a) => (
              <label key={a.key} className="notices-checkbox">
                <input
                  type="checkbox"
                  checked={form.areas.includes(a.key)}
                  onChange={() => toggleArea(a.key)}
                />
                <span>{t(a.labelKey)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="notices-field-row">
          <label className="notices-field">
            <span>{t('notices.startsLabel')}</span>
            <input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setField('starts_at', e.target.value)}
            />
          </label>
          <label className="notices-field">
            <span>{t('notices.endsLabel')}</span>
            <input
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setField('ends_at', e.target.value)}
            />
          </label>
        </div>

        <div className="notices-field-row">
          <label className="notices-field">
            <span>{t('notices.linkUrlLabel')}</span>
            <input
              type="text"
              value={form.link_url}
              onChange={(e) => setField('link_url', e.target.value)}
              placeholder={t('notices.linkUrlPlaceholder')}
            />
          </label>
          <label className="notices-field">
            <span>{t('notices.linkLabelLabel')}</span>
            <input
              type="text"
              value={form.link_label}
              onChange={(e) => setField('link_label', e.target.value)}
              maxLength={80}
              placeholder={t('notices.linkLabelPlaceholder')}
            />
          </label>
        </div>

        <div className="notices-field-row notices-toggles">
          <label className="notices-checkbox">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField('is_active', e.target.checked)}
            />
            <span>{t('notices.activeCheck')}</span>
          </label>
          <label className="notices-checkbox">
            <input
              type="checkbox"
              checked={form.dismissible}
              onChange={(e) => setField('dismissible', e.target.checked)}
            />
            <span>{t('notices.dismissibleCheck')}</span>
          </label>
        </div>

        <div className="notices-form-actions">
          <button type="submit" className="notices-btn notices-btn-primary" disabled={saving}>
            {saving ? t('settings.saving') : editingId ? t('notices.saveChanges') : t('notices.create')}
          </button>
          {editingId && (
            <button type="button" className="notices-btn notices-btn-ghost" onClick={startNew}>
              {t('matchDetail.cancel')}
            </button>
          )}
        </div>
      </form>

      <div className="notices-admin-list">
        <h3>{t('notices.allNotices', { count: notices.length })}</h3>
        {loading ? (
          <p className="notices-admin-empty">{t('live.loading')}</p>
        ) : notices.length === 0 ? (
          <p className="notices-admin-empty">{t('notices.empty')}</p>
        ) : (
          <table className="notices-table">
            <thead>
              <tr>
                <th>{t('notices.titleLabel')}</th>
                <th>{t('notices.severityLabel')}</th>
                <th>{t('notices.areasLabel')}</th>
                <th>{t('admin.colStatus')}</th>
                <th>{t('notices.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {notices.map((n) => {
                const sev = NOTICE_SEVERITY_MAP[n.severity] || NOTICE_SEVERITY_MAP.info;
                return (
                  <tr key={n.id} className={editingId === n.id ? 'is-editing' : ''}>
                    <td>{n.title}</td>
                    <td><span className={`notices-sev notices-sev-${n.severity}`}>{sev.emoji} {t(sev.labelKey)}</span></td>
                    <td className="notices-areas-cell">{areaSummary(n.areas)}</td>
                    <td>
                      <button
                        type="button"
                        className={`notices-pill ${n.is_live ? 'on' : 'off'}`}
                        onClick={() => handleToggleActive(n)}
                        title={t('notices.toggleHint')}
                      >
                        {n.is_live ? t('notices.live') : n.is_active ? t('notices.scheduled') : t('notices.off')}
                      </button>
                    </td>
                    <td className="notices-row-actions">
                      <button type="button" className="notices-btn notices-btn-small" onClick={() => startEdit(n)}>
                        {t('admin.edit')}
                      </button>
                      <button type="button" className="notices-btn notices-btn-small notices-btn-danger" onClick={() => handleDelete(n)}>
                        {t('admin.remove')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
